import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {AddLookupStepButtonMenu} from 'lookup-config/AddLookupStepButtonMenu';
import {Schema} from 'schemas/SchemaConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE_LIGHT, GRAY, GRAY_LIGHT} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';

import {
  LookupConfigModalContextInfo,
  FormErrors,
  LookupStepInfo,
  LookupStepType,
} from './LookupConfigModalConstants';
import {getFollowingStepTypes} from './LookupConfigUtils';
import validateLookupSteps from './validateLookupSteps';

export interface AddLookupStepButtonProps {
  disabled?: boolean;
  previousStepGroup: Array<LookupStepInfo>;
  setFormErrors: (formErrors: FormErrors) => void;
  handleSetLookupStep: (lookupStep: LookupStepInfo) => void;
  context?: LookupConfigModalContextInfo;
  allowedLookupStepTypes: Array<LookupStepType>;
  baseSchema: Schema | null;
}

export function AddLookupStepButton(props: AddLookupStepButtonProps): JSX.Element | null {
  const lastLookupStep = props.previousStepGroup[props.previousStepGroup.length - 1];
  const followingStepTypes = getFollowingStepTypes(lastLookupStep, props.allowedLookupStepTypes);
  const isDisabled = props.disabled || followingStepTypes.length === 0;
  return (
    <Dropdown
      alwaysDropUp={true}
      menu={
        <AddLookupStepButtonMenu
          handleSetLookupStep={props.handleSetLookupStep}
          context={props.context}
          followingStepTypes={followingStepTypes}
          lastLookupStep={lastLookupStep}
          setAndGetFormErrors={() => {
            const newFormErrors = validateLookupSteps(props.previousStepGroup, props.baseSchema);
            props.setFormErrors(newFormErrors);
            return newFormErrors;
          }}
        />
      }
    >
      <Button
        className={classNames(css(styles.addLookupStepButton), {
          [css(styles.addLookupStepButtonDisabled)]: isDisabled,
        })}
        disabled={isDisabled}
        icon="compose"
        text="Add lookup step"
        size={ButtonSize.SMALL}
        buttonStyle={ButtonStyle.SECONDARY_WHITE}
        dataTestAttrs={{component: 'AddLookupStepButton', element: 'button'}}
      />
    </Dropdown>
  );
}

const styles = StyleSheet.create({
  field: {
    width: '50%',
    marginBottom: '8px',
  },
  addLookupStepButton: {
    color: BLUE_LIGHT,
    fontWeight: 600,
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
    marginRight: '6px',
    marginLeft: '-9px',
    marginTop: '20px',
  },
  addLookupStepButtonDisabled: {
    color: GRAY,
  },
  lookupStepFormContainer: {
    marginTop: '20px',
    marginBottom: '20px',
  },
  lookupStepFormDivider: {
    borderTop: `1px solid ${GRAY_LIGHT}`,
    marginTop: '10px',
    marginBottom: '10px',
  },
});
